import React, { useRef, useState, useEffect, createRef } from 'react';
import { motion, useSpring, useTransform, useScroll } from 'framer-motion';
import CheckIcon from '@/assets/icons/check.svg?react';
import Frame1 from '@/assets/images/protectionCover1.png';
import Frame2 from '@/assets/images/protectionCover2.png';

const features = [
  { title: 'Maximize revenue', desc: 'Drive conversions, earn pure profit on plan sales, and keep customers coming back for more purchases.' },
  { title: 'Boost customer confidence', desc: 'Offer protection plans online, in-store, and post-purchase to help customers buy with confidence.' },
  { title: 'Easy integration', desc: 'Our technology is easy for merchants to implement and their customers to use.' },
];

const customerFeatures = [
  { title: 'Brand trust', desc: 'Build trust by offering protection for product failure or accidental damage like drops, spills, or scratches.' },
  { title: 'No-hassle claim resolution', desc: 'Improve the customer experience with 24/7 online support, no deductibles, and fast repairs and replacements.' },
  { title: 'Delightful experiences', desc: 'When customers enjoy your products, they’re more likely to become long-term brand advocates.' },
];

const BUFFER = 300;
const TOTAL_FEATURE_SCROLL = 2000;
const SCROLL_TOTAL = TOTAL_FEATURE_SCROLL + BUFFER;
const SLICE = TOTAL_FEATURE_SCROLL / features.length;
const SLICE_C = TOTAL_FEATURE_SCROLL / customerFeatures.length;

const ProtectionExperience: React.FC = () => {
  // merchant state & refs
  const merchantHeadingRef = useRef<HTMLHeadingElement>(null);
  const merchantContainerRef = useRef<HTMLDivElement>(null);
  const merchantDescRefs = useRef(features.map(() => createRef<HTMLDivElement>()));
  const [merchantRawY, setMerchantRawY] = useState(0);
  const [merchantProgress, setMerchantProgress] = useState<number[]>(features.map(() => 0));
  const [merchantDescHeights, setMerchantDescHeights] = useState<number[]>(features.map(() => 0));

  // customer state & refs
  const customerHeadingRef = useRef<HTMLHeadingElement>(null);
  const customerContainerRef = useRef<HTMLDivElement>(null);
  const customerDescRefs = useRef(customerFeatures.map(() => createRef<HTMLDivElement>()));
  const [customerRawY, setCustomerRawY] = useState(0);
  const [customerProgress, setCustomerProgress] = useState<number[]>(customerFeatures.map(() => 0));
  const [customerDescHeights, setCustomerDescHeights] = useState<number[]>(customerFeatures.map(() => 0));

  // floating cards spring
  const scrollSpring = useSpring(merchantRawY / SCROLL_TOTAL, { stiffness: 50, damping: 20 });
  const scaleX = useTransform(scrollSpring, [0, 0.5, 1], [0.9, 1, 1]);
  const scrollSpringC = useSpring(customerRawY / SCROLL_TOTAL, { stiffness: 50, damping: 20 });
  const scaleXC = useTransform(scrollSpringC, [0, 0.5, 1], [0.9, 1, 1]);

  // heading scroll transforms
  const { scrollYProgress: merchantHeadingProg } = useScroll({
    target: merchantHeadingRef,
    offset: ['start end', 'end start'],
  });
  const { scrollYProgress: customerHeadingProg } = useScroll({
    target: customerHeadingRef,
    offset: ['start end', 'end start'],
  });
  const merchantHeadingX = useTransform(merchantHeadingProg, [0, 0.9], ['-100px', '0px']);
  const merchantHeadingOpacity = useTransform(merchantHeadingProg, [0, 0.7], [0, 1]);
  const customerHeadingX = useTransform(customerHeadingProg, [0, 0.9], ['100px', '0px']);
  const customerHeadingOpacity = useTransform(customerHeadingProg, [0, 0.7], [0, 1]);

  // scroll listener for the cards
  useEffect(() => {
    const onScroll = () => {
      // merchant
      if (merchantContainerRef.current) {
        const startY = merchantContainerRef.current.offsetTop;
        const raw = window.scrollY - startY;
        const clamped = Math.min(Math.max(raw, 0), SCROLL_TOTAL);
        setMerchantRawY(clamped);
        setMerchantProgress(
          features.map((_, i) => {
            const start = SLICE * i;
            const end = start + SLICE;
            if (clamped <= start) return 0;
            if (clamped >= end) return 1;
            return (clamped - start) / SLICE;
          })
        );
      }
      // customer
      if (customerContainerRef.current) {
        const startYc = customerContainerRef.current.offsetTop;
        const rawC = window.scrollY - startYc;
        const clampedC = Math.min(Math.max(rawC, 0), SCROLL_TOTAL);
        setCustomerRawY(clampedC);
        setCustomerProgress(
          customerFeatures.map((_, i) => {
            const start = SLICE_C * i;
            const end = start + SLICE_C;
            if (clampedC <= start) return 0;
            if (clampedC >= end) return 1;
            return (clampedC - start) / SLICE_C;
          })
        );
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // measure desc heights
  useEffect(() => {
    setMerchantDescHeights(merchantDescRefs.current.map(r => r.current?.scrollHeight ?? 0));
    setCustomerDescHeights(customerDescRefs.current.map(r => r.current?.scrollHeight ?? 0));
  }, []);

  // media query
  const [isLgUp, setIsLgUp] = useState<boolean>(false);

  useEffect(() => {
    const checkScreenSize = () => setIsLgUp(window.innerWidth >= 1024);
    checkScreenSize(); // Initial check
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <section className="tw-bg-black tw-text-white tw-pt-24 tw-pb-32 tw-overflow-x-hidden lg:tw-overflow-visible">
      {/* Top Intro */}
      <div className="tw-flex tw-justify-center tw-px-4 tw-mx-auto tw-mb-24">
        <div className="tw-max-w-2xl tw-w-full">
          <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold">
            You’ve got 100+ reasons to believe.
          </h2>
          <p className="tw-text-lg md:tw-text-2xl tw-text-gray-400 tw-mt-4 tw-font-bold">
            Our merchants are our partners. Like Jai had Veeru, and Krishna had Arjun — {" "}
            <span className="tw-text-[#00C5FB]"> we’ve got you. </span>
            With over 100 trusted partnerships, we work side by side to help you grow your business and deliver great experiences to your customers.
          </p>
        </div>
      </div>


      {/* Merchant Heading */}
      <div className="tw-overflow-x-hidden">
        <motion.h3
          ref={merchantHeadingRef}
          className="tw-text-[80px] md:tw-text-[160px] lg:tw-text-[250px] tw-font-bold tw-text-gray-800/60 tw-select-none tw-whitespace-nowrap tw-leading-none tw-mb-8 tw-relative tw-z-10"
          style={{ x: merchantHeadingX, opacity: merchantHeadingOpacity }}
        >
          Merchant<br />experience
        </motion.h3>
      </div>

      {/* Merchant Scroll Section */}
      <div
        ref={merchantContainerRef}
        style={{ height: isLgUp ? `calc(100vh + ${SCROLL_TOTAL}px)` : 'auto' }}
        className="tw-relative"
      >
        <section className={`tw-flex tw-flex-col lg:tw-sticky lg:tw-top-0 lg:tw-flex-row tw-w-full ${isLgUp ? 'tw-h-screen' : ''}`}>
          {/* Image */}
          <div className="tw-w-full lg:tw-w-[55%] tw-h-full lg:tw-h-[100vh] tw-overflow-hidden tw-grayscale">
            <img src={Frame1} alt="Team planning" className="tw-object-cover tw-w-full tw-h-full tw-relative" />
          </div>
          {/* Content + Card */}
          <div className={`tw-w-full md:tw-w-full lg:tw-w-4/12 tw-flex tw-flex-col tw-p-8 tw-relative`}>
            <p className="tw-text-white/60 tw-my-8 lg:tw-my-24 tw-text-lg md:tw-text-xl tw-leading-snug">
              Get more from every purchase. Protega boosts profitability with increased average order values, conversions, and repeat transactions.
            </p>
            <motion.div
              style={{ scaleX, y: isLgUp ? '-20%' : 0, transformOrigin: 'left center' }}
              className="tw-hidden lg:tw-flex tw-bg-[#0f0f0f] tw-rounded-xl tw-p-6 tw-shadow-2xl tw-flex-col tw-space-y-4 tw-overflow-hidden tw-absolute tw-top-1/2 tw-left-[-40px]"
            >
              {features.map((f, i) => (
                <div key={i} className="tw-flex tw-items-start tw-space-x-3">
                  <div className="tw-w-2 tw-relative tw-rounded tw-self-stretch tw-bg-gray-600">
                    <div
                      className="tw-absolute tw-rounded"
                      style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        background: '#06b6d4',
                        height: `${merchantProgress[i] * 100}%`,
                      }}
                    />
                  </div>
                  <div className="tw-flex-1 tw-flex tw-flex-col">
                    <h3 className="tw-text-3xl tw-font-extrabold tw-text-white tw-mb-1">{f.title}</h3>
                    <div style={{ height: merchantDescHeights[i] * merchantProgress[i], overflow: 'hidden' }}>
                      <div ref={merchantDescRefs.current[i]}>
                        <p className="tw-text-gray-300 tw-text-base">{f.desc}</p>
                      </div>
                    </div>
                  </div>
                  <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center ${merchantProgress[i] === 1 ? 'tw-bg-[#00C5FB]' : 'tw-bg-gray-600'}`}>
                    {merchantProgress[i] === 1 && <CheckIcon className="tw-h-5" />}
                  </div>
                </div>
              ))}
            </motion.div>
            <div className="tw-block lg:tw-hidden tw-px-6 tw-mb-16">
              <div className="tw-bg-[#0f0f0f] tw-rounded-xl tw-p-6 tw-space-y-8 tw-shadow-lg">
                {features.map((f, i) => (
                  <div key={i} className="tw-flex tw-items-start tw-space-x-4">
                    <div className="tw-w-1.5 tw-rounded tw-bg-[#00C5FB] tw-mt-1 tw-self-stretch" />
                    <div className="tw-flex-1">
                      <h4 className="tw-text-3xl tw-font-extrabold tw-text-white tw-mb-1">{f.title}</h4>
                      <p className="tw-text-gray-300 tw-text-base">{f.desc}</p>
                    </div>
                    <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-full tw-bg-[#00C5FB] tw-flex tw-items-center tw-justify-center">
                      <CheckIcon className="tw-h-5 tw-w-5 tw-text-black" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Customer Heading */}
      <div className="tw-overflow-x-hidden">
        <motion.h3
          ref={customerHeadingRef}
          className="tw-mt-32 md:tw-mt-48 lg:tw-mt-64
               tw-text-[80px] md:tw-text-[160px]
               tw-text-right tw-font-bold tw-text-gray-800/60
               tw-select-none tw-whitespace-nowrap tw-leading-none
               tw-mb-8 tw-relative tw-z-10"
          style={{ x: customerHeadingX, opacity: customerHeadingOpacity }}
        >
          Customer<br />experience
        </motion.h3>
      </div>


      {/* Customer Scroll Section */}
      <div
        ref={customerContainerRef}
        style={{ height: isLgUp ? `calc(100vh + ${SCROLL_TOTAL}px)` : 'auto' }}
        className="tw-relative tw-z-20"
      >
        <section className={`tw-flex tw-flex-col lg:tw-sticky lg:tw-top-0 lg:tw-flex-row-reverse tw-w-full ${isLgUp ? 'tw-h-screen' : ''}`}>
          <div className="tw-w-full lg:tw-w-[55%] tw-h-full lg:tw-h-[100vh] tw-overflow-hidden tw-grayscale">
            <img src={Frame2} alt="Customer delight" className="tw-object-cover tw-w-full tw-h-full" />
          </div>
          <div className="tw-w-full md:tw-w-full lg:tw-w-4/12 tw-flex tw-flex-col tw-p-8 tw-relative">
            <p className="tw-text-white tw-my-8 tw-text-lg md:tw-text-xl tw-leading-snug">
              Protega offers customers brand confidence, hassle-free claims, and an overall delightful experience.
            </p>
            <motion.div
              style={{ scaleX: scaleXC, y: isLgUp ? '-20%' : 0, transformOrigin: 'left center' }}
              className="tw-hidden lg:tw-flex tw-bg-[#0f0f0f] tw-rounded-xl tw-p-6 tw-shadow-2xl tw-flex-col tw-space-y-4 tw-overflow-hidden tw-absolute tw-top-1/2 tw-right-[-30px]"
            >
              {customerFeatures.map((f, i) => (
                <div key={i} className="tw-flex tw-items-start tw-space-x-3">
                  <div className="tw-w-2 tw-relative tw-rounded tw-self-stretch tw-bg-gray-600">
                    <div
                      className="tw-absolute tw-rounded"
                      style={{
                        top: 0,
                        left: 0,
                        right: 0,
                        background: '#06b6d4',
                        height: `${customerProgress[i] * 100}%`,
                      }}
                    />
                  </div>
                  <div className="tw-flex-1 tw-flex tw-flex-col">
                    <h3 className="tw-text-3xl tw-font-extrabold tw-text-white tw-mb-1">{f.title}</h3>
                    <div style={{ height: customerDescHeights[i] * customerProgress[i], overflow: 'hidden' }}>
                      <div ref={customerDescRefs.current[i]}>
                        <p className="tw-text-gray-300 tw-text-base">{f.desc}</p>
                      </div>
                    </div>
                  </div>
                  <div className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center ${customerProgress[i] === 1 ? 'tw-bg-[#00C5FB]' : 'tw-bg-gray-600'}`}>
                    {customerProgress[i] === 1 && <CheckIcon className="tw-h-5" />}
                  </div>
                </div>
              ))}
            </motion.div>
            <div className="tw-block lg:tw-hidden tw-px-6 tw-mb-16">
              <div className="tw-bg-[#0f0f0f] tw-rounded-xl tw-p-6 tw-space-y-8 tw-shadow-lg">
                {customerFeatures.map((f, i) => (
                  <div key={i} className="tw-flex tw-items-start tw-space-x-4">
                    <div className="tw-w-1.5 tw-rounded tw-bg-[#00C5FB] tw-mt-1 tw-self-stretch" />
                    <div className="tw-flex-1">
                      <h4 className="tw-text-3xl tw-font-extrabold tw-text-white tw-mb-1">{f.title}</h4>
                      <p className="tw-text-gray-300 tw-text-base">{f.desc}</p>
                    </div>
                    <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-full tw-bg-[#00C5FB] tw-flex tw-items-center tw-justify-center">
                      <CheckIcon className="tw-h-5 tw-w-5 tw-text-black" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

    </section>
  );
};

export default ProtectionExperience;